'use client';

import { EPlatform } from '@/utils/enums/platform-enum';
import { useStore } from '@/providers/app-store-provider';
import React from 'react';
import dynamic from 'next/dynamic';
import CategoryNavBig from './components/CategoryNavBig/CategoryNavBig';

// ⚠️ 首屏 SSR 必现的板块统一改为静态 import：
// next/dynamic({ ssr: true }) 在客户端走 React.lazy，chunk 未就绪时会被 Suspense 拦截，
// 无 loading 时 promise 冒泡到顶层 page.tsx 的 <Suspense fallback={CriticalSkeleton}> 引发二次全页骨架，
// 加 loading 又会用占位替换 SSR 已渲染的真实 HTML 引发大规模闪动。
// 这些组件本就是首屏必需内容，直接走主 bundle，hydrate 立即可用，无 chunk 挂起。
import CategoryNavShape from './components/CategoryNavShape/CategoryNavShape';
import CategoryNavSmall from './components/CategoryNavSmall/CategoryNavSmall';
import FivePicSwiper from './components/FivePicSwiper/FivePicSwiper';
import HeroBanner from './components/HomeBanner/HomeBanner';
import MultiSwipe from './components/MultiSwipe/MultiSwipe';
import OneLineTwo from './components/OneLineTwo/OneLineTwo';
import ProductList from './components/ProductList';

// 仅 ssr:false 的板块保留 next/dynamic：它们本就不参与 SSR，
// 客户端首次走 loading 占位再渲染是预期行为，不会替换 SSR HTML，无闪动风险。
const BlogArticle = dynamic(() => import('./components/BlogArticle/BlogArticle'), {
  ssr: false,
  loading: () => <div className="h-[200px] animate-pulse bg-[#f5f5f5]" />,
});
const BrandImage = dynamic(() => import('./components/BrandImage/BrandImage'), {
  ssr: false,
  loading: () => <div className="h-[100px] animate-pulse bg-[#f5f5f5]" />,
});
const StyleHub = dynamic(() => import('./components/StyleHub/StyleHub'), {
  ssr: false,
  loading: () => <div className="h-[300px] animate-pulse bg-[#f5f5f5]" />,
});
const WaterFall = dynamic(() => import('./components/WaterFall/WaterFall'), {
  ssr: false,
  loading: () => <div className="h-[400px] animate-pulse bg-[#f5f5f5]" />,
});

interface HomePageBlockItem {
  id?: number;
  component_id?: string;
  jump_target?: any[];
  [key: string]: any;
}

interface HomeSectionRendererProps {
  listData: HomePageBlockItem[];
  deviceType: EPlatform;
  onTrack?: (eventParams: any) => void;
  onExpTrack?: (eventParams: any) => void;
  onValidExpTrack?: (eventParams: any) => void;
  homeListData?: HomePageBlockItem[];
}

const COMPONENT_MAP: Record<string, React.ComponentType<any>> = {
  swipeImage: HeroBanner,
  categoryNavSmall: CategoryNavSmall,
  categoryNavShape: CategoryNavShape,
  categoryNavBig: CategoryNavBig,
  styleHub: StyleHub,
  multiSwipe: MultiSwipe,
  oneLineTwo: OneLineTwo,
  multiSwipeSecond: FivePicSwiper,
  blogArticle: BlogArticle,
  brandImage: BrandImage,
  regularList: ProductList,
  waterFall: WaterFall,
};

export default function HomeSectionRenderer({
  listData,
  deviceType = EPlatform.WEB,
  onTrack,
  onExpTrack,
  onValidExpTrack,
  homeListData = [],
}: HomeSectionRendererProps) {
  const isMobile = deviceType === EPlatform.WAP;
  const homeAfterWaterFallStartIndex = useStore((s) => s.homeAfterWaterFallStartIndex);

  return (
    <>
      {listData.map((item, index) => {
        if (!item.component_id) return null;

        if (homeAfterWaterFallStartIndex !== null && index > homeAfterWaterFallStartIndex) {
          return null;
        }

        const Component = COMPONENT_MAP[item.component_id];
        if (!Component) return null;

        return (
          <div key={item.id || `section-${index}`} className="index-box">
            <Component
              moduleIndex={index}
              indexData={item}
              homeListData={homeListData}
              isMobile={isMobile}
              onTrack={onTrack}
              onExpTrack={onExpTrack}
              onValidExpTrack={onValidExpTrack}
            />
          </div>
        );
      })}
    </>
  );
}
